import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ICareMap } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { WithLinks } from '../caremap/WithLinks';
import { AlertPresenter } from '../AlertPresenter';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { Logger } from '../support/Logger';
import { MemoriesModel } from './MemoriesModel';

// Dual-scope factory (mirrors CaremapInsightsModelFactory for the care-recipient side, plus a
// personal side the care-map-only insights factory doesn't need): forActiveCaremap() serves the
// care profile's "What Bea remembers" section, forPersonal() serves the same UI off the user's own
// (caremap-independent) memories. Both hand back a MemoriesModel that the consuming component
// loads itself (via ensureLoaded()) -- unlike CaremapNotesModelFactory/CaremapInsightsModelFactory,
// this factory doesn't auto-load on access, since the personal scope has no "active caremap changed"
// trigger to hang that off of; consumers use useAsyncEffect instead (see MemoryList).
export class CaremapMemoriesModelFactory extends State<Record<string, never>> {
    static inject = () => [
        CareMapState,
        APIClient,
        AlertPresenter,
        BufferedEventBus,
        Logger
    ];
    constructor(
        public careMapState: CareMapState,
        public apiClient: APIClient,
        public alert: AlertPresenter,
        public events: BufferedEventBus,
        public logger: Logger
    ) {
        super({});

        // events allows a limited number of subscribers
        // so subscribe once here and pass the message on
        this.events.on('web-sockets.message', msg => {
            for (const model of Object.values(this.careMapIdToModel)) {
                model.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
            if (this.personalModel) {
                this.personalModel.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
        });
    }

    private careMapIdToModel: { [careMapId: string]: MemoriesModel } = {};
    private personalModel?: MemoriesModel;

    forActiveCaremap = (): MemoriesModel => {
        const activeCaremap = this.careMapState.getActiveCaremap() as WithLinks<ICareMap>;

        if (!activeCaremap) {
            throw new Error('there is no active caremap');
        }

        let model = this.careMapIdToModel[activeCaremap.careMapId!];
        if (!model) {
            model = new MemoriesModel('care-recipient', async () => activeCaremap.links.memories, this);
            this.careMapIdToModel[activeCaremap.careMapId!] = model;
        }

        return model;
    };

    forPersonal = (): MemoriesModel => {
        if (!this.personalModel) {
            this.personalModel = new MemoriesModel('personal', async () => (await this.apiClient.entry()).links.memories, this);
        }

        return this.personalModel;
    };
};
